import { Badge, Box, Divider, Group, Stack, Text, TextInput, Title } from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import { FiCalendar, FiFilter } from "react-icons/fi";
import { openapi } from "../../openapi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { DrofusChangeTypeFilter } from "../components/DrofusChangeTypeFilter";
import {
  DROFUS_CHANGES_DISCIPLINE_OPTIONS,
  setDrofusChangesDateFromInRedux,
  setDrofusChangesDateToInRedux,
  toggleDrofusChangesChangeTypeInRedux,
  toggleDrofusChangesAffectedDisciplineInRedux,
} from "./drofusChangesSlice";
import {
  selectDrofusChangesApiArg,
  selectDrofusChangesAffectedDisciplines,
  selectDrofusChangeEventMatchesActiveFilters,
  selectDrofusChangesSelectedChangeTypes,
} from "./drofusChangesSelectors";

export const DrofusChangesPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const { dateFrom, dateTo } = useAppSelector((state) => state.drofusChanges);
  const selectedChangeTypes = useAppSelector(selectDrofusChangesSelectedChangeTypes);
  const affectedDisciplines = useAppSelector(selectDrofusChangesAffectedDisciplines);
  const eventMatchesActiveFilters = useAppSelector(selectDrofusChangeEventMatchesActiveFilters);
  const apiArg = useAppSelector(selectDrofusChangesApiArg);
  const { data, isFetching, isError } = openapi.usePostMultiplacerDrofusInterfaceRoomChangesQuery(
    apiArg ?? skipToken
  );

  const filteredChangeCount = useMemo(
    () => data?.items.filter(eventMatchesActiveFilters).length ?? 0,
    [data?.items, eventMatchesActiveFilters]
  );

  return (
    <Stack
      gap="lg"
      h="100%"
      p="xl"
      style={{
        minHeight: 0,
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
        boxSizing: "border-box",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Box>
        <Group justify="space-between" align="flex-start" gap="xs" wrap="nowrap">
          <Box>
            <Title order={4}>Endringer</Title>
            <Text size="sm" c="dimmed">
              dRofus-endringer i valgte rom
            </Text>
          </Box>
          <Badge variant="light" color="blue">
            dRofus
          </Badge>
        </Group>
      </Box>

      <Divider />

      <Stack gap="sm">
        <Group gap="xs">
          <FiCalendar size={16} />
          <Text fw={600} size="sm">
            Dato
          </Text>
        </Group>

        <Group grow align="flex-start" gap="sm">
          <TextInput
            type="date"
            label="Fra"
            value={dateFrom}
            onChange={(event) =>
              dispatch(setDrofusChangesDateFromInRedux(event.currentTarget.value))
            }
            styles={{
              root: { minWidth: 0 },
              input: {
                backgroundColor: "#ffffff",
                borderColor: "#cbd5e1",
                height: 44,
                fontSize: 16,
              },
            }}
          />

          <TextInput
            type="date"
            label="Til"
            value={dateTo}
            onChange={(event) =>
              dispatch(setDrofusChangesDateToInRedux(event.currentTarget.value))
            }
            styles={{
              root: { minWidth: 0 },
              input: {
                backgroundColor: "#ffffff",
                borderColor: "#cbd5e1",
                height: 44,
                fontSize: 16,
              },
            }}
          />
        </Group>
      </Stack>

      <Divider />

      <Stack gap="xs">
        <Group gap="xs">
          <FiFilter size={16} />
          <Text fw={600} size="sm">
            Filter
          </Text>
        </Group>

        <Stack gap={8}>
          <Text size="xs" fw={700} c="dimmed">
            FAG
          </Text>
          <Group gap={6}>
            {DROFUS_CHANGES_DISCIPLINE_OPTIONS.map((option) => {
              const active = affectedDisciplines.includes(option.value);

              return (
                <Badge
                  key={option.value}
                  radius="sm"
                  size="lg"
                  styles={{
                    root: {
                      backgroundColor: active ? option.color : "#ffffff",
                      border: `1px solid ${option.color}`,
                      color: active ? "#ffffff" : option.color,
                      cursor: "pointer",
                    },
                    label: {
                      color: "inherit",
                    },
                  }}
                  onClick={() =>
                    dispatch(toggleDrofusChangesAffectedDisciplineInRedux(option.value))
                  }
                >
                  {option.label}
                </Badge>
              );
            })}
          </Group>
        </Stack>

        <Stack gap={6}>
          <Text size="xs" fw={700} c="dimmed">
            TYPE
          </Text>
          <DrofusChangeTypeFilter
            selectedChangeTypes={selectedChangeTypes}
            onToggleChangeType={(changeType) =>
              dispatch(toggleDrofusChangesChangeTypeInRedux(changeType))
            }
          />
        </Stack>

        <SummaryRow
          label="Endringer i intervallet"
          value={isError ? "Feil" : isFetching ? "Laster" : filteredChangeCount}
          color={isError ? "red" : isFetching ? "gray" : "blue"}
        />
      </Stack>
    </Stack>
  );
};

const SummaryRow: React.FC<{
  label: string;
  value: string | number;
  color: "blue" | "gray" | "red";
}> = ({ label, value, color }) => (
  <Group
    justify="space-between"
    gap="xs"
    px="md"
    py="sm"
    style={{
      background: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderRadius: 8,
    }}
  >
    <Text size="sm" c="#475569" fw={600}>
      {label}
    </Text>
    <Badge variant="light" color={color} size="lg">
      {value}
    </Badge>
  </Group>
);
